/**
 * 缓存管理器
 * 双层缓存：L1 内存缓存 + L2 sessionStorage 持久化
 * 支持后台定时刷新 + 页面可见性监听
 */

// 默认缓存过期时间（10分钟）
const DEFAULT_EXPIRE_TIME = 10 * 60 * 1000

// 缓存 key 前缀，避免与其他 sessionStorage 数据冲突
const CACHE_PREFIX = 'gxq_cache_'

// L1 内存缓存
const memoryCache = new Map()

// 追踪所有缓存 key（用于后台刷新）
const cacheKeys = new Set()

// 存储请求配置（用于静默刷新）
const cacheConfigs = new Map()

// 定时器引用
let refreshTimer = null

// axios 实例引用（在 request.js 中设置）
let axiosInstance = null

/**
 * 设置 axios 实例（供 request.js 调用）
 */
export function setAxiosInstance(instance) {
  axiosInstance = instance
}

/**
 * 生成缓存 key
 * @param {object} config - axios 请求配置
 * @returns {string} 缓存 key
 */
export function generateCacheKey(config) {
  const { url, method, data, params } = config
  const keyData = {
    url,
    method: method || 'get',
    data: data || null,
    params: params || null
  }
  // 使用 JSON 序列化生成唯一 key
  return CACHE_PREFIX + JSON.stringify(keyData)
}

/**
 * 检查缓存是否过期
 * @param {object} cacheItem - 缓存项
 * @returns {boolean} 是否过期
 */
function isExpired(cacheItem) {
  if (!cacheItem || !cacheItem.timestamp || !cacheItem.expireTime) {
    return true
  }
  return Date.now() - cacheItem.timestamp > cacheItem.expireTime
}

/**
 * 检查缓存是否即将过期（剩余时间 < 2分钟）
 * @param {object} cacheItem - 缓存项
 * @returns {boolean} 是否即将过期
 */
function isNearExpire(cacheItem) {
  if (!cacheItem || !cacheItem.timestamp || !cacheItem.expireTime) {
    return true
  }
  const remainingTime = cacheItem.timestamp + cacheItem.expireTime - Date.now()
  return remainingTime < 2 * 60 * 1000
}

/**
 * 获取缓存（双层读取）
 * @param {string} key - 缓存 key
 * @returns {object|null} 缓存数据或 null
 */
export function getCache(key) {
  // L1: 检查内存缓存
  const memoryData = memoryCache.get(key)
  if (memoryData && !isExpired(memoryData)) {
    return memoryData
  }

  // L2: 检查 sessionStorage
  try {
    const sessionData = sessionStorage.getItem(key)
    if (sessionData) {
      const parsed = JSON.parse(sessionData)
      if (!isExpired(parsed)) {
        // 回写 L1 内存缓存
        memoryCache.set(key, parsed)
        return parsed
      }
    }
  } catch (e) {
    // sessionStorage 读取失败，忽略
    console.warn('[Cache] sessionStorage read error:', e)
  }

  return null
}

/**
 * 设置缓存（双层写入）
 * @param {string} key - 缓存 key
 * @param {any} data - 缓存数据
 * @param {number} expireTime - 过期时间（毫秒）
 * @param {object} config - 原始请求配置
 */
export function setCache(key, data, expireTime = DEFAULT_EXPIRE_TIME, config = null) {
  const cacheItem = {
    data,
    timestamp: Date.now(),
    expireTime
  }

  // L1: 写入内存缓存
  memoryCache.set(key, cacheItem)

  // L2: 写入 sessionStorage
  try {
    sessionStorage.setItem(key, JSON.stringify(cacheItem))
  } catch (e) {
    // sessionStorage 写入失败（可能容量不足），降级为仅内存缓存
    console.warn('[Cache] sessionStorage write error, fallback to memory only:', e)
  }

  // 追踪缓存 key
  cacheKeys.add(key)

  // 存储请求配置（用于静默刷新）
  if (config) {
    cacheConfigs.set(key, config)
  }
}

/**
 * 清除指定缓存
 * @param {string} key - 缓存 key（可选，不传则清除所有）
 */
export function clearCache(key) {
  if (key) {
    memoryCache.delete(key)
    sessionStorage.removeItem(key)
    cacheKeys.delete(key)
    cacheConfigs.delete(key)
  } else {
    // 清除所有缓存
    memoryCache.clear()
    cacheKeys.forEach(k => {
      sessionStorage.removeItem(k)
    })
    cacheKeys.clear()
    cacheConfigs.clear()
  }
}

/**
 * 静默刷新单个缓存
 * @param {string} key - 缓存 key
 */
async function silentRefresh(key) {
  const config = cacheConfigs.get(key)
  if (!config || !axiosInstance) {
    return
  }

  try {
    // 复制配置，跳过缓存标记
    const refreshConfig = {
      ...config,
      skipCache: true,
      _isRefresh: true // 标记为刷新请求
    }
    
    const response = await axiosInstance(refreshConfig)
    
    if (response && response.status === 200) {
      // 更新缓存
      setCache(key, response.data, config.cacheTime || DEFAULT_EXPIRE_TIME, config)
    }
  } catch (e) {
    // 刷新失败，保留旧缓存
    console.warn('[Cache] Silent refresh failed:', e)
  }
}

/**
 * 刷新过期缓存
 */
function refreshExpiredCache() {
  cacheKeys.forEach(key => {
    const cache = getCache(key)
    if (!cache || isNearExpire(cache)) {
      silentRefresh(key)
    }
  })
}

/**
 * 初始化刷新机制
 */
function initRefreshMechanism() {
  // 定时刷新（10分钟）
  refreshTimer = setInterval(refreshExpiredCache, DEFAULT_EXPIRE_TIME)

  // 页面可见性监听（处理后台标签页定时器节流）
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        // 页面从后台切换回来，检查并刷新过期缓存
        refreshExpiredCache()
      }
    })
  }
}

/**
 * 销毁缓存管理器
 */
export function destroyCache() {
  if (refreshTimer) {
    clearInterval(refreshTimer)
    refreshTimer = null
  }
  clearCache()
}

// 初始化
initRefreshMechanism()

// 导出默认过期时间
export { DEFAULT_EXPIRE_TIME }
